import React, { useState } from 'react';

import queryString from 'query-string';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bool, shape, string, func } from 'prop-types';

import hasApiErrors from '../../common/utils/utils';
import ShowPasswordByIcon from '../../common/ShowPasswordByIcon';
import { PATH_LOGIN } from '../../constants';
import { expiredPasswordUpdate, resetPassword } from '../../api';
import { showResetPasswordMsg } from '../../store/actions/userState';
import {
  onlyRequiredValidationRules,
  passwordValidationRules,
} from '../../constants/UserConstants';
import {
  Button,
  ErrorMsg,
  InputFormField,
  PageLoader,
  Status,
} from '../../common';

const ResetPasswordForm = ({ isPasswordExpired, userData, onResetSuccess }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { register, handleSubmit, errors, watch, reset, setError } = useForm({
    mode: 'onChange',
  });

  const [isLoading, setIsLoading] = useState({
    loader: true,
    type: 'page',
  });
  const [visibleState, setVisibleState] = useState({
    old: false,
    newPass: false,
    confirm: false,
  });
  const serachParameter = history.location.search;

  const [apiError, setApiError] = useState({});
  const params = queryString.parse(serachParameter);

  const onSubmit = (data) => {
    setIsLoading({ loader: true, type: 'button' });

    if (isPasswordExpired) {
      const formData = {
        email: userData.email,
        old_password: data.old_password,
        new_password: data.new_password,
        confirm_new_password: data.confirm_password,
      };
      expiredPasswordUpdate(formData).then((response) => {
        if (response && response.status === 400) {
          setApiError(response && response.data);
          Object.keys(response.data).forEach((key) => {
            setError(key, {
              type: 'manual',
              message: Array.isArray(response.data[key])
                ? response.data[key][0]
                : response.data[key],
            });
          });
          setIsLoading({ loader: false, type: 'button' });
        } else if (response && response.status === 200) {
          dispatch(showResetPasswordMsg());
          reset();
          onResetSuccess();
          setIsLoading({ loader: false, type: 'button' });
        }
      });
    } else {
      const formData = {
        password: data.new_password,
        key: params.key,
      };
      resetPassword(formData).then((response) => {
        if (response && response.status === 400) {
          setApiError(response && response.data);
          setIsLoading({ loader: false, type: 'button' });
        } else if (response && response.status === 200) {
          dispatch(showResetPasswordMsg());
          history.push(PATH_LOGIN);
          setIsLoading({ loader: false, type: 'button' });
          reset();
        }
      });
    }
  };

  const handleChange = (event) => {
    setApiError({
      [event.target.name]: '',
    });
  };

  const toggleVisibility = (field, isOpen) => {
    setVisibleState((prev) => ({
      ...prev,
      [field]: isOpen,
    }));
  };

  return (
    <div>
      <h2 className="mb-0">Set your Password</h2>
      <p className="small-para">Please enter your password to proceed. </p>
      {isPasswordExpired && (
        <Status
          label={
            "Your password is over 365 days old. To keep your account secure, you're required to set your password."
          }
          type="agreement"
          className="status-nowrap capluse-status mb-3"
          icon="true"
          fontIcon="fa-regular fa-info-circle"
          labelColor="#9c6d17ff"
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <InputFormField className="mt-5">
          {isPasswordExpired && (
            <InputFormField>
              <label htmlFor="old_password">
                Old password
                <br />
                <input
                  className={
                    errors && errors.email
                      ? 'form-control'
                      : 'form-control mb-2'
                  }
                  type={visibleState.old ? 'text' : 'password'}
                  id="old_password"
                  name="old_password"
                  placeholder="Enter old password"
                  ref={register(onlyRequiredValidationRules)}
                  onChange={(event) => handleChange(event)}
                />
                <ShowPasswordByIcon
                  isPasswordEyeOpen={visibleState.old}
                  setIsPasswordEyeOpen={(val) => toggleVisibility('old', val)}
                  value={watch('old_password')}
                />
              </label>
              <ErrorMsg>
                {errors && errors.old_password && errors.old_password.message}
              </ErrorMsg>
              <br />
            </InputFormField>
          )}
          <InputFormField>
            <label htmlFor="password">
              New password
              <br />
              <input
                className={
                  errors && errors.email ? 'form-control' : 'form-control mb-2'
                }
                type={visibleState.newPass ? 'text' : 'password'}
                id="new_password"
                name="new_password"
                placeholder="Enter new password"
                ref={register(passwordValidationRules)}
                onChange={(event) => handleChange(event)}
              />
              <ShowPasswordByIcon
                isPasswordEyeOpen={visibleState.newPass}
                setIsPasswordEyeOpen={(val) => toggleVisibility('newPass', val)}
                value={watch('new_password')}
              />
            </label>
          </InputFormField>
          <ErrorMsg>
            {errors && errors.new_password && errors.new_password.message}
          </ErrorMsg>
          <br />
          <InputFormField>
            <label htmlFor="confirm_password">
              Confirm password
              <br />
              <input
                className="form-control"
                id="confirm_password"
                name="confirm_password"
                type={visibleState.confirm ? 'text' : 'password'}
                placeholder="Enter confirm password"
                ref={register({
                  required: {
                    value: true,
                    message: 'This field is required.',
                  },
                  validate: (value) =>
                    value === watch('new_password') ||
                    'The passwords do not match!',
                })}
                onChange={(event) => handleChange(event)}
              />
              <ShowPasswordByIcon
                isPasswordEyeOpen={visibleState.confirm}
                setIsPasswordEyeOpen={(val) => toggleVisibility('confirm', val)}
                value={watch('confirm_password')}
              />
            </label>
          </InputFormField>
          <ErrorMsg>
            {errors &&
              errors.confirm_password &&
              errors.confirm_password.message}
          </ErrorMsg>
          <ErrorMsg
            dangerouslySetInnerHTML={{
              __html:
                (apiError?.password &&
                  (Array.isArray(apiError.password)
                    ? apiError.password[0]
                    : apiError.password)) ||
                (apiError?.key &&
                  (Array.isArray(apiError.key)
                    ? apiError.key[0]
                    : apiError.key)),
            }}
          />
          <Button
            className="btn btn-primary w-100 mt-5"
            disabled={hasApiErrors(errors)}
          >
            {isLoading.loader && isLoading.type === 'button' ? (
              <PageLoader color="#FFF" type="button" />
            ) : (
              'Submit'
            )}
          </Button>
        </InputFormField>
      </form>
    </div>
  );
};

export default ResetPasswordForm;

ResetPasswordForm.defaultProps = {
  isPasswordExpired: false,
  email: '',
  userData: {
    email: '',
    customer: '',
  },
  onResetSuccess: () => {},
};

ResetPasswordForm.propTypes = {
  isPasswordExpired: bool,
  email: string,
  userData: shape({
    email: string,
    customer: string,
  }),
  onResetSuccess: func,
};
